import { useState, useCallback, useRef, useEffect } from "react";
import Cropper from "react-easy-crop";
import type { Area, Point } from "react-easy-crop";
import { Upload, Pencil, Trash2, ZoomIn, ZoomOut, RotateCw, Loader2, UserCircle } from "lucide-react";
import { uploadApi } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";

// ─── Load image helper ─────────────────────────────────────────────────────
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// ─── Canvas crop helper (drawImage approach — reliable for all sizes) ───────
async function getCroppedBlob(imageSrc: string, pixelCrop: Area, rotation = 0): Promise<Blob> {
  const image = await loadImage(imageSrc);

  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d")!;

  if (rotation === 0) {
    // Simple direct clip — no rotation needed
    ctx.drawImage(
      image,
      pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
      0, 0, pixelCrop.width, pixelCrop.height,
    );
  } else {
    // Render rotated image to a temp canvas, then clip from it
    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));
    const tmp = document.createElement("canvas");
    tmp.width = safeArea; tmp.height = safeArea;
    const tCtx = tmp.getContext("2d")!;
    tCtx.translate(safeArea / 2, safeArea / 2);
    tCtx.rotate((rotation * Math.PI) / 180);
    tCtx.translate(-safeArea / 2, -safeArea / 2);
    tCtx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5);
    ctx.drawImage(
      tmp,
      safeArea / 2 - image.width * 0.5 + pixelCrop.x,
      safeArea / 2 - image.height * 0.5 + pixelCrop.y,
      pixelCrop.width, pixelCrop.height,
      0, 0, pixelCrop.width, pixelCrop.height,
    );
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error("Canvas toBlob failed")),
      "image/jpeg", 0.92,
    );
  });
}

// ─── Small preview blob (480×270, faster for live card preview) ────────────
async function getPreviewBlob(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = 480; canvas.height = 270;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, 480, 270,
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error("Preview toBlob failed")),
      "image/jpeg", 0.7,
    );
  });
}

// ─── Props ─────────────────────────────────────────────────────────────────
interface PhotoCropUploadProps {
  value: string;
  onChange: (url: string) => void;
  shape?: "circle" | "rect";
  aspect?: number;
  label?: string;
  previewClassName?: string;
  postPreview?: boolean;
}

// ─── Component ─────────────────────────────────────────────────────────────
export function PhotoCropUpload({
  value,
  onChange,
  shape = "circle",
  aspect = 1,
  label = "Foto",
  previewClassName = "w-20 h-20 rounded-full",
  postPreview = false,
}: PhotoCropUploadProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modal state
  const [open, setOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [uploading, setUploading] = useState(false);

  const [livePreviewUrl, setLivePreviewUrl] = useState<string>('');
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const generateLivePreview = useCallback(async (src: string, pixels: Area) => {
    if (!src || !pixels.width) return;
    try {
      const blob = await getPreviewBlob(src, pixels);
      const url = URL.createObjectURL(blob);
      setLivePreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch { /**/ }
  }, []);

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedArea(croppedAreaPixels);
    if (postPreview && imageSrc) {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
      previewTimerRef.current = setTimeout(() => {
        generateLivePreview(imageSrc, croppedAreaPixels);
      }, 350);
    }
  }, [postPreview, imageSrc, generateLivePreview]);

  useEffect(() => () => { if (livePreviewUrl) URL.revokeObjectURL(livePreviewUrl); }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setOpen(true);
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be re-selected
    e.target.value = "";
  };

  const handleConfirm = async () => {
    if (!imageSrc || !croppedArea) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedArea, rotation);
      const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
      const url = await uploadApi.image(file);
      onChange(url);
      setOpen(false);
      setImageSrc(null);
      toast({ title: "Foto enviada com sucesso!" });
    } catch {
      toast({ title: "Erro ao enviar foto", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setImageSrc(null);
  };

  // Fetch remote URL as blob so canvas doesn't get tainted by CORS.
  // Uses relative path (pathname only) so the Vite dev proxy intercepts it
  // and in production same-origin requests work naturally.
  const fetchAsBlobUrl = async (url: string): Promise<string> => {
    if (url.startsWith('data:') || url.startsWith('blob:')) return url;
    // Convert absolute URL → relative path so same-origin rules apply in both dev and prod
    let fetchUrl = url;
    try {
      const parsed = new URL(url);
      fetchUrl = parsed.pathname + parsed.search;
    } catch { /* keep original if not a valid absolute URL */ }
    const res = await fetch(fetchUrl);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  };

  const handleRecrop = async () => {
    if (!value) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    try {
      const blobUrl = await fetchAsBlobUrl(value);
      setImageSrc(blobUrl);
    } catch {
      setImageSrc(value); // fallback
    }
    setOpen(true);
  };

  return (
    <>
      {/* Preview + buttons */}
      <div className="flex items-center gap-4">
        {/* Photo preview */}
        <div className={`${previewClassName} bg-gray-100 flex items-center justify-center overflow-hidden shrink-0`}>
          {value ? (
            <img
              src={value}
              alt={label}
              className="w-full h-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
            />
          ) : (
            <UserCircle className="w-8 h-8 text-gray-300" />
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 bg-brand-primary/8 hover:bg-brand-primary/15 text-brand-primary text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            {value ? "Trocar foto" : "Enviar foto"}
          </button>
          {value && (
            <>
              <button
                type="button"
                onClick={handleRecrop}
                className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Reencaixar
              </button>
              <button
                type="button"
                onClick={() => onChange("")}
                className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-500 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remover
              </button>
            </>
          )}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Crop modal — rendered as a fixed overlay */}
      {open && imageSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleCancel} />

          {/* Modal */}
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800 text-sm">
                {postPreview ? "Ajustar imagem do post" : "Ajustar foto"}
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {postPreview
                  ? "Selecione a área que aparecerá no card do post (16:9)"
                  : "Arraste para posicionar · use o zoom para ajustar"}
              </p>
            </div>

            {/* Crop area */}
            <div className="relative w-full bg-gray-900" style={{ height: 320 }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspect}
                cropShape={shape}
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Controls */}
            <div className="px-5 py-4 space-y-3 bg-gray-50 border-t border-gray-100">
              {/* Zoom slider */}
              <div className="flex items-center gap-3">
                <ZoomOut className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 accent-brand-primary h-1.5 rounded-full cursor-pointer"
                />
                <ZoomIn className="w-4 h-4 text-gray-400 shrink-0" />
              </div>

              {/* Rotation */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Rotação: {rotation}°</span>
                <button
                  type="button"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-brand-primary bg-white border border-gray-200 hover:border-brand-primary/40 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                  Girar 90°
                </button>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={uploading}
                  className="flex-1 flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-60"
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />Enviando...</>
                  ) : (
                    "Confirmar"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={uploading}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
